/* ==========================================================================
   Mereldon cart drawer
   - "Complete the look": ranked, size-aware recommendations that never vanish
     when the drawer re-renders (quantity change, new product, size swap).
   - Line items: size can be changed in place.
   ========================================================================== */

const MRL_RECS = {
  section: 'cart-drawer-recommendations',
  show: 4,
  candidatesToRender: 7,
  storageKey: 'mrl-cart-recs-v2',
  storageTtl: 20 * 60 * 1000,
};

const MRL_CATEGORY_RULES = [
  ['set', /\b(set|sets|co-?ord|two[- ]piece)\b/],
  ['jumpsuit', /(jumpsuit|playsuit|romper)/],
  ['bag', /\b(bag|bags|clutch|purse|tote|handbag|crossbody)\b/],
  ['dress', /(dress|gown)/],
  ['bottom', /(skirt|pant|trouser|jean|short|legging)/],
  ['top', /(top|blouse|shirt|cami|bodysuit|tee|knit|sweater|cardigan|corset|bustier|jacket|blazer)/],
];

// What completes the look for each category in the bag (collection handles, in priority order).
const MRL_COMPLEMENTS = {
  dress: ['bags'],
  set: ['bags'],
  jumpsuit: ['bags'],
  top: ['skirts', 'bottoms', 'bags'],
  bottom: ['tops', 'bags'],
  bag: ['dresses'],
  other: ['bags'],
};
const MRL_COLLECTION_CATEGORY = { bags: 'bag', skirts: 'bottom', bottoms: 'bottom', tops: 'top', dresses: 'dress' };

const MRL_OCCASIONS = ['wedding', 'guest', 'race', 'evening', 'party', 'cocktail', 'formal', 'gala', 'summer', 'beach', 'holiday', 'brunch', 'date', 'garden'];
const MRL_NEUTRALS = /(black|white|cream|ivory|beige|nude|tan|camel|brown|gold|silver|champagne|natural|stone)/;

function mrlCategory(type, title) {
  const t = String(type || '').toLowerCase();
  for (const [cat, re] of MRL_CATEGORY_RULES) if (re.test(t)) return cat;
  const n = String(title || '').toLowerCase();
  for (const [cat, re] of MRL_CATEGORY_RULES) if (re.test(n)) return cat;
  return 'other';
}

function mrlNormSize(value) {
  const v = String(value || '').toLowerCase().replace(/\s+/g, '');
  return { xxs: '2xs', xxl: '2xl', xxxl: '3xl', xxxxl: '4xl' }[v] || v;
}

function mrlNorm(value) {
  return String(value || '').toLowerCase().trim();
}

function mrlRoot() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

class MereldonCartRecommendations {
  constructor(drawer) {
    this.drawer = drawer;
    this.jsonCache = new Map();
    this.cardCache = new Map();
    this.runId = 0;
    this.timer = null;
    this.lastSignature = null;
    this.lastHTML = '';
    this.restore();
  }

  /* ---------- persistence (instant paint on next page) ---------- */
  restore() {
    try {
      const raw = window.sessionStorage.getItem(MRL_RECS.storageKey);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data && Date.now() - data.t < MRL_RECS.storageTtl && data.root === mrlRoot()) {
        this.lastSignature = data.sig;
        this.lastHTML = data.html || '';
      }
    } catch (e) {
      /* storage unavailable */
    }
  }

  persist() {
    try {
      window.sessionStorage.setItem(
        MRL_RECS.storageKey,
        JSON.stringify({ sig: this.lastSignature, html: this.lastHTML, t: Date.now(), root: mrlRoot() })
      );
    } catch (e) {
      /* storage unavailable */
    }
  }

  /* ---------- DOM wiring ---------- */
  slot() {
    return this.drawer.querySelector('[data-cart-recommendations]');
  }

  observe() {
    if (this.observer) return;
    this.observer = new MutationObserver(() => {
      const slot = this.slot();
      if (slot && !slot.dataset.mrlState) {
        this.paint(slot, this.lastHTML);
        this.schedule(30);
      }
    });
    this.observer.observe(this.drawer, { childList: true, subtree: true });
  }

  schedule(delay = 60) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), delay);
  }

  readCart() {
    return [...this.drawer.querySelectorAll('.mrl-line[data-product-id]')].map((el) => ({
      id: Number(el.dataset.productId),
      handle: el.dataset.handle || '',
      title: el.dataset.title || '',
      type: el.dataset.productType || '',
      tags: mrlNorm(el.dataset.tags),
      size: mrlNormSize(el.dataset.size),
      colour: mrlNorm(el.dataset.colour),
    }));
  }

  signature(items) {
    const ids = [...new Set(items.map((i) => i.id))].sort().join(',');
    const sizes = [...new Set(items.map((i) => i.size).filter(Boolean))].sort().join(',');
    return `${mrlRoot()}|${ids}|${sizes}`;
  }

  // Paint a block of cards into the slot, dropping anything that is already in the bag.
  paint(slot, html) {
    if (!slot) return;
    const inCart = new Set(this.readCart().map((i) => String(i.id)));
    slot.dataset.mrlState = 'painted';
    if (!html) {
      if (slot.innerHTML !== '') slot.innerHTML = '';
      return;
    }
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    tpl.content.querySelectorAll('.mrl-rec').forEach((card) => {
      if (inCart.has(card.dataset.productId)) card.remove();
    });
    const cards = tpl.content.querySelectorAll('.mrl-rec');
    [...cards].slice(MRL_RECS.show).forEach((card) => card.remove());
    if (!cards.length) {
      slot.innerHTML = '';
      return;
    }
    slot.replaceChildren(tpl.content);
    this.updateArrows(slot);
  }

  updateArrows(slot) {
    const track = slot.querySelector('.mrl-recs__track');
    const prev = slot.querySelector('[data-mrl-recs-prev]');
    const next = slot.querySelector('[data-mrl-recs-next]');
    if (!track || !prev || !next) return;
    const sync = () => {
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    track.addEventListener('scroll', sync, { passive: true });
    requestAnimationFrame(sync);
  }

  /* ---------- data ---------- */
  getJSON(url) {
    if (!this.jsonCache.has(url)) {
      const promise = fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .catch(() => {
          this.jsonCache.delete(url);
          return null;
        });
      this.jsonCache.set(url, promise);
    }
    return this.jsonCache.get(url);
  }

  normalize(json) {
    const list = (json && json.products) || [];
    return list
      .map((p) => {
        const optionNames = (p.options || []).map((o) => mrlNorm(typeof o === 'string' ? o : o.name));
        const variants = (p.variants || []).map((v) => ({
          id: v.id,
          available: v.available !== false,
          options: Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter((x) => x != null),
        }));
        const tags = Array.isArray(p.tags) ? p.tags.join(',') : String(p.tags || '');
        return {
          id: Number(p.id),
          handle: p.handle,
          title: p.title || '',
          type: p.type || p.product_type || '',
          tags: mrlNorm(tags),
          optionNames,
          variants,
        };
      })
      .filter((p) => p.id && p.handle && p.variants.length);
  }

  async collectCandidates(cart) {
    const root = mrlRoot();
    const anchors = [];
    const seenAnchor = new Set();
    for (const item of cart) {
      if (seenAnchor.has(item.id)) continue;
      seenAnchor.add(item.id);
      anchors.push(item);
      if (anchors.length === 3) break;
    }

    const cartCategories = new Set(cart.map((i) => mrlCategory(i.type, i.title)));
    const sources = [];
    anchors.forEach((anchor, anchorIndex) => {
      const anchorWeight = 1 - anchorIndex * 0.15;
      sources.push({
        url: `${root}recommendations/products.json?product_id=${anchor.id}&limit=10&intent=complementary`,
        weight: 7 * anchorWeight,
        decay: 0.05,
      });
      sources.push({
        url: `${root}recommendations/products.json?product_id=${anchor.id}&limit=10&intent=related`,
        weight: 4 * anchorWeight,
        decay: 0.06,
      });
    });

    const complementHandles = [];
    cartCategories.forEach((cat) => {
      (MRL_COMPLEMENTS[cat] || MRL_COMPLEMENTS.other).forEach((handle) => {
        if (cartCategories.has(MRL_COLLECTION_CATEGORY[handle])) return; // already has one of these
        if (!complementHandles.includes(handle)) complementHandles.push(handle);
      });
    });
    complementHandles.slice(0, 3).forEach((handle, index) => {
      sources.push({
        url: `${root}collections/${handle}/products.json?limit=24`,
        weight: 6 - index * 0.75,
        decay: 0.015,
        category: MRL_COLLECTION_CATEGORY[handle],
      });
    });
    sources.push({ url: `${root}collections/new-arrivals/products.json?limit=24`, weight: 1.5, decay: 0.01 });

    const results = await Promise.all(sources.map((s) => this.getJSON(s.url).then((json) => ({ s, json }))));
    if (results.every((r) => r.json === null)) throw new Error('recommendations offline');

    const map = new Map();
    results.forEach(({ s, json }) => {
      this.normalize(json).forEach((product, rank) => {
        const points = s.weight * Math.max(0.35, 1 - rank * s.decay);
        const entry = map.get(product.id) || { product, source: 0 };
        entry.source += points;
        map.set(product.id, entry);
      });
    });
    return map;
  }

  rank(cart, candidates) {
    const inCart = new Set(cart.map((i) => i.id));
    const sizes = new Set(cart.map((i) => i.size).filter(Boolean));
    const colours = new Set(cart.map((i) => i.colour).filter(Boolean));
    const cartText = cart.map((i) => `${i.handle} ${i.tags} ${i.title}`.toLowerCase()).join(' ');
    const cartOccasions = MRL_OCCASIONS.filter((w) => cartText.includes(w));
    const scored = [];

    candidates.forEach(({ product, source }) => {
      if (inCart.has(product.id)) return;
      if (/gift-?card/.test(product.handle) || /\b(no-upsell|hidden)\b/.test(product.tags)) return;

      const sizeIdx = product.optionNames.findIndex((n) => /size|tamanho|talla/.test(n));
      const colourIdx = product.optionNames.findIndex((n) => /colo|^cor$/.test(n));
      const available = product.variants.filter((v) => v.available);
      if (!available.length) return;

      let best = null;
      let bestFit = -Infinity;
      available.forEach((v, index) => {
        let fit = -index * 0.001;
        if (sizeIdx >= 0 && sizes.size && sizes.has(mrlNormSize(v.options[sizeIdx]))) fit += 3;
        if (colourIdx >= 0) {
          const c = mrlNorm(v.options[colourIdx]);
          if (colours.has(c)) fit += 1;
          else if (MRL_NEUTRALS.test(c)) fit += 0.6;
        }
        if (fit > bestFit) {
          bestFit = fit;
          best = v;
        }
      });

      let score = source;
      if (sizeIdx >= 0 && sizes.size) score += bestFit >= 3 ? 2.5 : -2.5;
      if (colourIdx >= 0) {
        const c = mrlNorm(best.options[colourIdx]);
        if (colours.has(c)) score += 1.2;
        else if (MRL_NEUTRALS.test(c)) score += 0.6;
      }
      const text = `${product.handle} ${product.tags} ${product.title}`.toLowerCase();
      score += Math.min(1.8, cartOccasions.filter((w) => text.includes(w)).length * 0.6);

      scored.push({
        product,
        variantId: best.id,
        category: mrlCategory(product.type, product.title),
        score,
      });
    });

    // Greedy pick with category diversity so the row is not four of the same thing.
    const picks = [];
    const used = new Map();
    const pool = scored.slice();
    while (pool.length && picks.length < MRL_RECS.candidatesToRender) {
      let bestIndex = 0;
      let bestValue = -Infinity;
      pool.forEach((c, i) => {
        const value = c.score - (used.get(c.category) || 0) * 2.2;
        if (value > bestValue) {
          bestValue = value;
          bestIndex = i;
        }
      });
      const [chosen] = pool.splice(bestIndex, 1);
      picks.push(chosen);
      used.set(chosen.category, (used.get(chosen.category) || 0) + 1);
    }
    return picks;
  }

  card(pick) {
    const key = `${mrlRoot()}${pick.product.handle}:${pick.variantId}`;
    if (!this.cardCache.has(key)) {
      const url = `${mrlRoot()}products/${encodeURIComponent(pick.product.handle)}?variant=${pick.variantId}&section_id=${MRL_RECS.section}`;
      const promise = fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const card = doc.querySelector('.mrl-rec');
          return card ? card.outerHTML : null;
        })
        .catch(() => {
          this.cardCache.delete(key);
          return null;
        });
      this.cardCache.set(key, promise);
    }
    return this.cardCache.get(key);
  }

  block(cards) {
    return `<section class="mrl-recs" aria-label="Complete the look">
  <div class="mrl-recs__head">
    <h3 class="mrl-recs__title">Complete the look</h3>
    <div class="mrl-recs__nav">
      <button type="button" class="mrl-recs__arrow" data-mrl-recs-prev aria-label="Previous"><svg viewBox="0 0 8 12" width="7" height="11" aria-hidden="true"><path d="M6.5 1l-5 5 5 5" fill="none" stroke="currentColor" stroke-width="1.4"/></svg></button>
      <button type="button" class="mrl-recs__arrow" data-mrl-recs-next aria-label="Next"><svg viewBox="0 0 8 12" width="7" height="11" aria-hidden="true"><path d="M1.5 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.4"/></svg></button>
    </div>
  </div>
  <div class="mrl-recs__track">${cards.join('')}</div>
</section>`;
  }

  async refresh() {
    const runId = ++this.runId;
    const cart = this.readCart();
    if (!cart.length) return;
    const signature = this.signature(cart);

    if (signature === this.lastSignature) {
      const slot = this.slot();
      if (slot && slot.dataset.mrlState !== 'fresh') {
        this.paint(slot, this.lastHTML);
        slot.dataset.mrlState = 'fresh';
      }
      return;
    }

    let picks;
    try {
      picks = this.rank(cart, await this.collectCandidates(cart));
    } catch (e) {
      return; // offline: keep whatever is painted
    }
    if (runId !== this.runId) return;

    const rendered = await Promise.all(picks.map((p) => this.card(p)));
    if (runId !== this.runId) return;

    const cards = rendered.filter(Boolean).slice(0, MRL_RECS.show);
    this.lastSignature = signature;
    this.lastHTML = cards.length ? this.block(cards) : '';
    this.persist();

    const slot = this.slot();
    if (slot) {
      this.paint(slot, this.lastHTML);
      slot.dataset.mrlState = 'fresh';
    }
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.onDrawerClick.bind(this));
    // Capture phase: size selects must never reach CartItems' quantity "change" handler.
    this.addEventListener('change', this.onDrawerChange.bind(this), true);
    this.recommendations = new MereldonCartRecommendations(this);
  }

  connectedCallback() {
    this.recommendations.observe();
    const slot = this.recommendations.slot();
    if (slot && !slot.dataset.mrlState) this.recommendations.paint(slot, this.recommendations.lastHTML);
    const warm = () => this.recommendations.schedule(0);
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2500 });
    else setTimeout(warm, 1200);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.loadRecommendations();
  }

  // Kept for cart.js and other scripts that call it after a cart change.
  loadRecommendations() {
    this.recommendations.schedule(0);
  }

  onDrawerClick(event) {
    const addButton = event.target.closest('[data-mrl-rec-add]');
    if (addButton && this.contains(addButton)) {
      this.addRecommendation(addButton);
      return;
    }
    const arrow = event.target.closest('[data-mrl-recs-prev], [data-mrl-recs-next]');
    if (arrow) {
      const track = arrow.closest('.mrl-recs')?.querySelector('.mrl-recs__track');
      if (!track) return;
      const direction = arrow.hasAttribute('data-mrl-recs-next') ? 1 : -1;
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    }
  }

  onDrawerChange(event) {
    const target = event.target;
    if (!target.matches('[data-mrl-rec-variant], [data-mrl-line-variant]')) return;
    event.stopPropagation();
    if (target.matches('[data-mrl-rec-variant]')) {
      const option = target.selectedOptions[0];
      const card = target.closest('.mrl-rec');
      const price = card?.querySelector('[data-mrl-rec-price]');
      if (option && price) {
        price.innerHTML = `${option.dataset.compare ? `<s>${option.dataset.compare}</s>` : ''}<span>${option.dataset.price}</span>`;
      }
      const add = card?.querySelector('[data-mrl-rec-add]');
      if (add) add.dataset.variantId = target.value;
      return;
    }
    if (target.matches('[data-mrl-line-variant]')) this.swapLineVariant(target);
  }

  addRecommendation(button) {
    if (button.disabled) return;
    const card = button.closest('.mrl-rec');
    const select = card?.querySelector('[data-mrl-rec-variant]');
    const variantId = Number(select ? select.value : button.dataset.variantId);
    const error = card?.querySelector('.mrl-rec__error');
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
    if (error) error.hidden = true;

    fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status || state.errors) throw new Error(state.description || state.message || 'Unable to add');
        this.renderContents(state);
      })
      .catch((e) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = 'Add';
        if (error) {
          error.textContent = e.message && e.message !== 'Unable to add' ? e.message : 'Sorry, this size just sold out.';
          error.hidden = false;
        }
      });
  }

  swapLineVariant(select) {
    const line = select.closest('.mrl-line');
    const previous = select.dataset.current;
    const nextId = Number(select.value);
    if (!line || !nextId || String(nextId) === previous) return;

    const quantity = Number(line.dataset.quantity) || 1;
    let properties = {};
    try {
      properties = JSON.parse(line.dataset.properties || '{}') || {};
    } catch (e) {
      properties = {};
    }
    const errorText = line.querySelector('.cart-item__error-text');
    const spinner = line.querySelector('.loading__spinner');
    select.disabled = true;
    spinner?.classList.remove('hidden');
    if (errorText) errorText.textContent = '';

    let added = false;
    fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({ items: [{ id: nextId, quantity, properties }] }),
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status || result.errors) throw new Error(result.description || result.message || '');
        added = true;
        return fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: line.dataset.lineKey,
            quantity: 0,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
      })
      .then((response) => response.json())
      .then((state) => {
        if (state.status || state.errors) throw new Error(state.description || state.message || '');
        this.renderContents(state);
      })
      .catch((e) => {
        if (added) {
          this.reloadDrawer();
          return;
        }
        select.value = previous;
        select.disabled = false;
        spinner?.classList.add('hidden');
        if (errorText) errorText.textContent = e.message || 'That size is not available right now.';
      });
  }

  reloadDrawer() {
    fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => this.renderContents({ sections }))
      .catch(() => window.location.reload());
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const isEmpty = !this.querySelector('.mrl-line');
    this.classList.toggle('is-empty', isEmpty);

    document.dispatchEvent(
      new CustomEvent('mereldon:cart-updated', {
        detail: { welcome10Applied: Boolean(this.querySelector('[data-welcome10-applied]')) },
      })
    );

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.loadRecommendations();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
